import { useEffect, useRef, useState } from 'react';

interface UseScrollAnimationOptions {
    threshold?: number;
    rootMargin?: string;
    triggerOnce?: boolean;
}

export const useScrollAnimation = (options: UseScrollAnimationOptions = {}) => {
    const { threshold = 0.1, rootMargin = '0px', triggerOnce = true } = options;
    const ref = useRef<HTMLDivElement>(null);
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        const element = ref.current;
        if (!element) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                    if (triggerOnce) {
                        observer.unobserve(element);
                    }
                } else if (!triggerOnce) {
                    setIsVisible(false);
                }
            },
            { threshold, rootMargin }
        );

        observer.observe(element);
        return () => observer.disconnect();
    }, [threshold, rootMargin, triggerOnce]);

    return { ref, isVisible };
};

// Animated section wrapper component
interface AnimatedSectionProps {
    children: React.ReactNode;
    className?: string;
    animation?: 'fade-up' | 'fade-in' | 'fade-left' | 'fade-right' | 'scale-in';
    delay?: number;
    duration?: number;
}

export const AnimatedSection: React.FC<AnimatedSectionProps> = ({
    children,
    className = '',
    animation = 'fade-up',
    delay = 0,
    duration = 700,
}) => {
    const { ref, isVisible } = useScrollAnimation({ threshold: 0.1 });

    const baseStyles: React.CSSProperties = {
        transitionProperty: 'opacity, transform',
        transitionDuration: `${duration}ms`,
        transitionTimingFunction: 'cubic-bezier(0.25, 0.46, 0.45, 0.94)',
        transitionDelay: `${delay}ms`,
    };

    const animations = {
        'fade-up': {
            hidden: { opacity: 0, transform: 'translateY(40px)' },
            visible: { opacity: 1, transform: 'translateY(0)' },
        },
        'fade-in': {
            hidden: { opacity: 0, transform: 'scale(0.98)' },
            visible: { opacity: 1, transform: 'scale(1)' },
        },
        'fade-left': {
            hidden: { opacity: 0, transform: 'translateX(-40px)' },
            visible: { opacity: 1, transform: 'translateX(0)' },
        },
        'fade-right': {
            hidden: { opacity: 0, transform: 'translateX(40px)' },
            visible: { opacity: 1, transform: 'translateX(0)' },
        },
        'scale-in': {
            hidden: { opacity: 0, transform: 'scale(0.9)' },
            visible: { opacity: 1, transform: 'scale(1)' },
        },
    };

    const animState = isVisible ? animations[animation].visible : animations[animation].hidden;

    return (
        <div
            ref={ref}
            className={className}
            style={{
                ...baseStyles,
                ...animState,
            }}
        >
            {children}
        </div>
    );
};

// Staggered children animation wrapper
interface StaggeredContainerProps {
    children: React.ReactNode;
    className?: string;
    staggerDelay?: number;
    animation?: 'fade-up' | 'fade-in' | 'fade-left' | 'fade-right' | 'scale-in';
}

export const StaggeredContainer: React.FC<StaggeredContainerProps> = ({
    children,
    className = '',
    staggerDelay = 100,
    animation = 'fade-up',
}) => {
    const { ref, isVisible } = useScrollAnimation({ threshold: 0.1 });

    return (
        <div ref={ref} className={className}>
            {Array.isArray(children)
                ? children.map((child, index) => (
                    <AnimatedSection
                        key={index}
                        animation={animation}
                        delay={isVisible ? index * staggerDelay : 0}
                    >
                        {child}
                    </AnimatedSection>
                ))
                : children
            }
        </div>
    );
};
